import { CATALOG_PROVIDER_KEYS, getCatalogProviderManifest, } from "./contracts.js";
import { DATA_CATALOG_TEMPLATE_LABELS, normalizeDataCatalogEntryAttrs, renderCatalogInlineText, renderCatalogTemplateMarkdown, } from "./embed-rendering.js";
export const CATALOG_EMBED_RUNTIME_ATTR_KEYS = [
    "status",
    "lastCheckedAt",
    "lastSyncedAt",
    "brokenReason",
];
/**
 * Sourced 1:1 from the canonicalized node attr's `status` field. Phase 2 code
 * only ever observes "current" | "stale" | "broken" — nothing writes
 * "unverifiable" to that attr until Phase 3's refresh pipeline exists.
 * Declared 4-wide now so Phase 3 requires zero changes here: every consumer
 * must treat this as an open-ended value (default/fallback branch), never an
 * exhaustive switch over exactly 3 members.
 *
 * This array is the single source: the type below, every other status union in
 * the monorepo, and the Convex validator are all derived from it.
 */
export const CATALOG_EMBED_STATUSES = ["current", "stale", "broken", "unverifiable"];
const STATUS_PHRASES = {
    current: "",
    stale: " (stale — refresh available)",
    broken: " (broken — object not found)",
    unverifiable: " (unverifiable — refresh pending)",
};
export function isCatalogEmbedNodeType(type) {
    return type === "dataCatalogEntry" || type === "dataCatalogTemplateEntry";
}
function titleCase(value) {
    return value.length > 0 ? value.charAt(0).toUpperCase() + value.slice(1) : value;
}
/** Fixed provider display label — never the connection's user-editable
 *  displayName, so scoring text doesn't shift meaning when a connection is
 *  renamed. Reuses the existing provider manifest registry rather than
 *  introducing a duplicate label table. An unrecognized key (should be
 *  structurally impossible) falls back to a title-cased render rather than
 *  throwing — a single malformed embed must not break scoring for the rest
 *  of the entity. */
function resolveProviderLabel(providerKey) {
    return CATALOG_PROVIDER_KEYS.includes(providerKey)
        ? getCatalogProviderManifest(providerKey).displayName
        : titleCase(providerKey);
}
function formatFieldValue(value) {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : "(not set)";
}
/**
 * Deterministic, total, never throws. No timestamps, no randomness, no
 * locale-dependent formatting — field order is exactly the persisted attr
 * array order (never re-sorted), provider label is a fixed lookup, and the
 * status suffix is a fixed phrase keyed only on the status enum. brokenReason
 * (raw, potentially upstream-sourced text) is never interpolated.
 */
export function buildCatalogEvidenceScoringText(attrs) {
    const providerLabel = resolveProviderLabel(attrs.providerKey);
    const objectLabel = attrs.objectDisplayLabel.trim() || attrs.objectStableRef;
    const subject = attrs.embedKind === "template"
        ? `${providerLabel} ${attrs.templateLabel.trim() ||
            (attrs.templateKey ? DATA_CATALOG_TEMPLATE_LABELS[attrs.templateKey] : "") ||
            "Catalog reference"} for "${objectLabel}"`
        : [providerLabel, attrs.objectCategory.trim(), `"${objectLabel}"`]
            .filter((part) => part.length > 0)
            .join(" ");
    const fieldSuffix = attrs.selectedFieldKeys.length === 0
        ? ""
        : `: ${attrs.selectedFieldKeys
            .map((key, index) => {
            const label = attrs.selectedFieldLabels[index]?.trim() || key;
            const value = formatFieldValue(attrs.selectedFieldValues[index] ?? "");
            return `${label} = ${value}`;
        })
            .join("; ")}`;
    const statusSuffix = STATUS_PHRASES[attrs.status] ?? "";
    return `${subject}${fieldSuffix}${statusSuffix}`;
}
/**
 * Mirrors the frontend's parseDataCatalogEntry validity gate
 * (apps/web-app/.../extract-catalog-embeds.ts) — a catalog node missing any
 * of these five identity fields is malformed/incomplete and must not
 * surface as evidence. normalizeDataCatalogEntryAttrs defaults every string
 * field to "" rather than throwing, so without this gate a corrupt node
 * would silently produce a phantom evidence item instead of being dropped.
 */
export function isValidCatalogEmbedAttrs(attrs) {
    return (attrs.embedId.trim().length > 0 &&
        attrs.connectionId.trim().length > 0 &&
        attrs.catalogDisplayName.trim().length > 0 &&
        attrs.providerKey.trim().length > 0 &&
        attrs.objectStableRef.trim().length > 0);
}
/**
 * Renders just the markdown for a single catalog node — for call sites that
 * only need renderedText (richTextMarkdown.ts, draftMarkdownRuntime.ts,
 * richDoc.ts) and would otherwise pay for evidence/scoringText construction
 * they never use. Byte-identical to walkNodeForCatalogEmbedContent's own
 * renderedText for the same node.
 */
export function renderCatalogEmbedMarkdown(node) {
    if (node.type === "dataCatalogTemplateEntry") {
        return renderCatalogTemplateMarkdown({ ...(node.attrs ?? {}), embedKind: "template" });
    }
    if (node.type === "dataCatalogEntry") {
        return renderCatalogInlineText(node.attrs ?? {});
    }
    return "";
}
export function buildCatalogEvidenceItem(args) {
    const { attrs } = args;
    return {
        stableRef: attrs.objectStableRef,
        provider: attrs.providerKey,
        sourceFieldId: args.sourceFieldId,
        embedId: attrs.embedId,
        nodeType: args.nodeType,
        embedKind: attrs.embedKind,
        status: attrs.status,
        lastResolvedAt: attrs.lastSyncedAt,
        lastCheckedAt: attrs.lastCheckedAt,
        scoringText: buildCatalogEvidenceScoringText(attrs),
        labels: attrs.selectedFieldLabels.length > 0 ? attrs.selectedFieldLabels : [attrs.objectDisplayLabel],
        externalUrl: null,
    };
}
/**
 * Walks a single ProseMirror node subtree (typically one field's content),
 * producing the full triple for every dataCatalogEntry/dataCatalogTemplateEntry
 * found within it. Pure — no Convex, no I/O. Reusable by the frontend
 * (markdown preview/export) and by Convex (via the document-level wrapper in
 * packages/convex/convex/lib/catalogContent/).
 */
export function walkNodeForCatalogEmbedContent(node, sourceFieldId) {
    if (isCatalogEmbedNodeType(node.type)) {
        const renderedText = renderCatalogEmbedMarkdown(node);
        const attrs = normalizeDataCatalogEntryAttrs(node.attrs ?? {});
        if (!isValidCatalogEmbedAttrs(attrs)) {
            return { renderedText, scoringText: "", evidence: [] };
        }
        const evidence = buildCatalogEvidenceItem({ attrs, nodeType: node.type, sourceFieldId });
        return { renderedText, scoringText: evidence.scoringText, evidence: [evidence] };
    }
    if (!Array.isArray(node.content)) {
        return { renderedText: "", scoringText: "", evidence: [] };
    }
    const childResults = node.content.map((child) => walkNodeForCatalogEmbedContent(child, sourceFieldId));
    return {
        renderedText: childResults.map((result) => result.renderedText).join(""),
        scoringText: childResults
            .map((result) => result.scoringText)
            .filter((text) => text.length > 0)
            .join("\n"),
        evidence: childResults.flatMap((result) => result.evidence),
    };
}
